// components/StockNotes.js
import React, { useEffect, useCallback, useReducer } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import {
    Box,
    Heading,
    Textarea,
    Button,
    Spinner,
    Alert,
    VStack,
    HStack,
    Text
} from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'react-toastify';

// --- Mock API functions (Replace with actual fetch calls) ---
// You'll need to implement error handling and use the access token
const mockApi = {
    getNote: async (ticker, userId, /* token */) => {
        console.log(`MOCK API: Fetching note for ${userId} and ${ticker}`);
        // Simulate network delay
        await new Promise(res => setTimeout(res, 500));
        const note = localStorage.getItem(`note_${userId}_${ticker}`);
        if (note) {
            return { content: note };
        } else {
            // Simulate 404 Not Found
            const err = new Error("Not Found");
            err.status = 404;
            throw err;
        }
    },
    saveNote: async (ticker, userId, content, /* token */) => {
        console.log(`MOCK API: Saving note for ${userId} and ${ticker}`);
        // Simulate network delay
        await new Promise(res => setTimeout(res, 500));
        localStorage.setItem(`note_${userId}_${ticker}`, content);
        // Simulate success
        return { ok: true };
    }
};
// --- End Mock API ---

const initialNoteState = {
    noteContent: '',
    savedNote: null,
    isEditing: false,
    isFetchingNote: false,
    isSaving: false,
    error: null,
};

function noteReducer(state, action) {
    switch (action.type) {
        case 'RESET':
            return { ...initialNoteState };
        case 'FETCH_START':
            return { ...state, isFetchingNote: true, error: null };
        case 'FETCH_SUCCESS':
            return {
                ...state,
                isFetchingNote: false,
                savedNote: action.content,
                noteContent: action.content,
                isEditing: false,
            };
        case 'FETCH_EMPTY':
            return {
                ...state,
                isFetchingNote: false,
                savedNote: '',
                noteContent: '',
                isEditing: true,
            };
        case 'FETCH_ERROR':
            return {
                ...state,
                isFetchingNote: false,
                error: action.message,
                savedNote: null,
                noteContent: '',
                isEditing: false,
            };
        case 'SET_NOTE_CONTENT':
            return { ...state, noteContent: action.content };
        case 'START_EDIT':
            return { ...state, isEditing: true };
        case 'CANCEL_EDIT':
            return {
                ...state,
                noteContent: state.savedNote ?? '',
                isEditing: false,
                error: null,
            };
        case 'SAVE_START':
            return { ...state, isSaving: true, error: null };
        case 'SAVE_SUCCESS':
            return {
                ...state,
                savedNote: action.content,
                isEditing: false,
                isSaving: false,
            };
        case 'SAVE_ERROR':
            return {
                ...state,
                isSaving: false,
                error: action.message,
            };
        default:
            return state;
    }
}

function StockNotes({ ticker }) {
    const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth0();

    const [{ noteContent, savedNote, isEditing, isFetchingNote, isSaving, error }, dispatch] = useReducer(
        noteReducer,
        initialNoteState
    );

    // --- Fetch Note Function ---
    const fetchNote = useCallback(async () => {
        if (!isAuthenticated || !user?.sub || !ticker) {
            return; // Don't fetch if not logged in or ticker is missing
        }
        dispatch({ type: 'FETCH_START' });
        try {
            // TODO: Replace mockApi.getNote with actual fetch
            // const token = await getAccessTokenSilently();
            // const response = await fetch(`/api/notes/${ticker}`, {
            //     headers: { Authorization: `Bearer ${token}` }
            // });
            // if (!response.ok) {
            //     if (response.status === 404) { // Note doesn't exist yet
            //         setSavedNote('');
            //         setNoteContent('');
            //         setIsEditing(true); // Start editing if no note exists
            //     } else {
            //         throw new Error(`Failed to fetch note: ${response.statusText}`);
            //     }
            // } else {
            //     const data = await response.json();
            //     setSavedNote(data.content);
            //     setNoteContent(data.content);
            //     setIsEditing(false);
            // }

            // --- Using Mock API ---
            const data = await mockApi.getNote(ticker, user.sub /*, token */);
            dispatch({ type: 'FETCH_SUCCESS', content: data.content });
            // --- End Using Mock API ---

        } catch (err) {
            if (err.status === 404) { // Handle "Not Found" specifically
                dispatch({ type: 'FETCH_EMPTY' });
                console.log("No existing note found, starting edit mode.");
            } else {
                console.error("Error fetching note:", err);
                dispatch({
                    type: 'FETCH_ERROR',
                    message: "Failed to load notes. Please try again later.",
                });
            }
        }
    }, [ticker, isAuthenticated, user?.sub /*, getAccessTokenSilently */]); // Add getAccessTokenSilently if using real API

    // --- Fetch note on mount or when user/ticker changes ---
    useEffect(() => {
        if (!isAuthLoading && isAuthenticated) {
            fetchNote();
        } else if (!isAuthLoading && !isAuthenticated) {
            dispatch({ type: 'RESET' });
        }
    }, [fetchNote, isAuthenticated, isAuthLoading]);

    // --- Save Note Function ---
    const handleSave = async () => {
        if (!isAuthenticated || !user?.sub) return;
        dispatch({ type: 'SAVE_START' });
        try {
            // TODO: Replace mockApi.saveNote with actual fetch
            // const token = await getAccessTokenSilently();
            // const response = await fetch(`/api/notes/${ticker}`, {
            //     method: 'PUT',
            //     headers: {
            //         'Content-Type': 'application/json',
            //         Authorization: `Bearer ${token}`
            //     },
            //     body: JSON.stringify({ content: noteContent })
            // });
            // if (!response.ok) {
            //     throw new Error(`Failed to save note: ${response.statusText}`);
            // }
            // // Successfully saved
            // setSavedNote(noteContent);
            // setIsEditing(false);
            // toast({ title: "Note saved!", status: "success", duration: 2000, isClosable: true });

            // --- Using Mock API ---
            await mockApi.saveNote(ticker, user.sub, noteContent /*, token */);
            dispatch({ type: 'SAVE_SUCCESS', content: noteContent });
            toast("Note saved!");
            // --- End Using Mock API ---

        } catch (err) {
            console.error("Error saving note:", err);
            dispatch({
                type: 'SAVE_ERROR',
                message: "Failed to save note. Please try again.",
            });
            toast("Save failed");
        }
    };

    // --- Edit/Cancel ---
    const handleEdit = () => {
        dispatch({ type: 'START_EDIT' });
    };

    const handleCancel = () => {
        dispatch({ type: 'CANCEL_EDIT' });
    };

    // --- Render Logic ---
    if (isAuthLoading || isFetchingNote) {
        return (
            <Box borderWidth="1px" borderRadius="lg" p={4}>
                <Heading size="md" mb={4}>Notes</Heading>
                <Spinner />
                <Text ml={2} as="span">Loading notes…</Text>
            </Box>
        );
    }

    if (!isAuthenticated) {
        return (
            <Box borderWidth="1px" borderRadius="lg" p={4}>
                <Heading size="md" mb={4}>Notes</Heading>
                <Text>Please log in to view and edit notes.</Text>
                {/* Optionally include your LoginButton here */}
                {/* <LoginButton /> */}
            </Box>
        );
    }

    // Main content for logged-in users
    return (
        <Box borderWidth="1px" borderRadius="lg" p={4}>
            <Heading size="md" mb={4}>Notes</Heading>

            {error && (
                <Alert status="error" mb={4}>
                    <Alert.Indicator />
                    {error}
                </Alert>
            )}

            {isEditing ? (
                // --- Edit Mode ---
                <VStack spacing={4} align="stretch">
                    <Textarea
                        placeholder="Write your notes here... (Markdown supported)"
                        value={noteContent}
                        onChange={(e) => dispatch({ type: 'SET_NOTE_CONTENT', content: e.target.value })}
                        minHeight="200px" // Give it some decent height
                        disabled={isSaving}
                    />
                    <HStack>
                        <Button
                            colorScheme="blue"
                            onClick={handleSave}
                            loading={isSaving}
                            disabled={isSaving}
                        >
                            Save Note
                        </Button>
                        <Button variant="ghost" onClick={handleCancel} disabled={isSaving}>
                            Cancel
                        </Button>
                    </HStack>
                </VStack>
            ) : (
                // --- View Mode ---
                <Box>
                    {savedNote ? (
                        // Using Chakra's Box with prose class for basic styling (if theme supports it)
                        // or apply custom styling.
                         <Box className="markdown-content" mb={4}>
                             <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                 {savedNote}
                             </ReactMarkdown>
                         </Box>
                    ) : (
                        <Text fontStyle="italic" color="gray.500" mb={4}>
                            No notes saved yet for {ticker.toUpperCase()}.
                        </Text>
                    )}
                    <Button onClick={handleEdit} size="sm">
                        Edit Notes
                    </Button>
                </Box>
            )}
        </Box>
    );
}

export default StockNotes;
